/**
 * Copyright 2025-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useRef, useState } from "react";
import { NetworkStatus } from "@apollo/client";
import {
  ReportStatus,
  useResourceComplianceReportForUiQuery,
} from "~/generated/graphql";

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 15;

export function useResourceComplianceReportForUI(input: {
  resourceId: string;
  startDate: string;
  endDate: string;
}) {
  const [attempt, setAttempt] = useState(0);
  const [timedOut, setTimedOut] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data, loading, error, refetch, networkStatus } =
    useResourceComplianceReportForUiQuery({
      variables: {
        input: {
          resourceId: input.resourceId,
          dateRange: {
            startDate: input.startDate,
            endDate: input.endDate,
          },
        },
      },
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
    });

  const status = data?.resourceComplianceReportForUI?.status;

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (status === ReportStatus.InProgress) {
      if (attempt >= MAX_ATTEMPTS) {
        setTimedOut(true);
        return;
      }
      timerRef.current = setTimeout(() => {
        setAttempt((prev) => prev + 1);
        refetch();
      }, POLL_INTERVAL_MS);
    } else {
      setTimedOut(false);
      setAttempt(0);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [status, attempt, refetch]);

  const retry = () => {
    setTimedOut(false);
    setAttempt(0);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    refetch();
  };

  const packages = data?.resourceComplianceReportForUI?.packages ?? [];
  const actionsWithMissingData =
    data?.resourceComplianceReportForUI?.actionsWithMissingData ?? [];

  const isCompleted = status === ReportStatus.Completed;
  const isFailed = status === ReportStatus.Failed;
  const isPolling = status === ReportStatus.InProgress && !timedOut;
  const isRefetching = networkStatus === NetworkStatus.refetch;
  const isInitialLoad = loading && !data;
  const isFetchingInProgress =
    status === ReportStatus.InProgress && (loading || isRefetching);
  const isLoading = isInitialLoad || isFetchingInProgress;

  return {
    packages,
    actionsWithMissingData,
    status,
    loading: isLoading,
    error,
    isPolling,
    timedOut,
    isCompleted,
    isFailed,
    retry,
  };
}
